import React, { FunctionComponent } from 'react'
import { useT } from '@transifex/react'
import { Link } from '@lightspeed/design-system-react'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import {
  openFulfilmentLineItemPopover,
  selectLineItemQuantityInputError,
} from '../../../../state/slices/uiStateSlice'
import {
  useSaleInventoryData,
  useUserManager,
} from '../../../../ng-compat/hooks'
import { ERRORS } from '../../../../constants/sale-form-errors'
import {
  isNonFulfillmentOnAccountSale,
  isUnfulfilledLineItem,
} from '../../../../functions/sale'
import {
  selectFulfilmentDetails,
  selectHasCompletedFulfilments,
  selectSaleStatus,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { LineItem } from '../../../../models/line-item'
import { PERMISSIONS } from '../../../../constants'
import { selectSaleReturnFor } from '../../../../state/selectors'
import { InputErrorMessage } from './InputErrorMessage'

interface Props {
  lineItem: LineItem
}

export const LineItemQuantityError: FunctionComponent<Props> = ({
  lineItem,
}) => {
  const t = useT()
  const dispatch = useAppDispatch()
  const saleStatus = useAppSelector(selectSaleStatus)
  const fulfillmentDetails = useAppSelector(selectFulfilmentDetails)
  const hasCompletedFulfilments = useAppSelector(selectHasCompletedFulfilments)
  const isReferencedReturn = !!useAppSelector(selectSaleReturnFor)

  const saleInventoryData = useSaleInventoryData()
  const userManager = useUserManager()

  const lineItemQuantityError = useAppSelector(
    selectLineItemQuantityInputError(lineItem.localId)
  )

  if (lineItemQuantityError === ERRORS.NO_INVENTORY) {
    const inventoryLevel =
      saleInventoryData.getInventoryData(lineItem)?.inventoryLevel ?? 0

    if (
      !getFeatureFromCache('fulfillment_lif_b3') ||
      isNonFulfillmentOnAccountSale({
        status: saleStatus,
        fulfilmentDetails: fulfillmentDetails,
      }) ||
      hasCompletedFulfilments ||
      isReferencedReturn
    )
      return (
        <InputErrorMessage
          errorElement={
            <>
              {t('You only have {inventoryLevel} available. ', {
                _context: 'Not enough inventory error',
                inventoryLevel,
              })}
              <Link
                href="#"
                onClick={() => {
                  saleInventoryData.disableInventoryValidation(lineItem)
                }}
                data-cy="ignore-low-stock-warning"
                isSecondary={true}
                data-track="ignore-low-stock-warning"
              >
                {t('Continue anyway?')}
              </Link>
            </>
          }
        />
      )

    // Fulfillment popover warning label
    return (
      <div data-cy="low-stock-warning">
        <InputErrorMessage
          errorElement={
            <>
              {t('There’s {inventoryLevel} available. ', {
                inventoryLevel,
              })}
              <Link
                href="#"
                onClick={() => {
                  dispatch(openFulfilmentLineItemPopover(lineItem.localId))
                }}
                data-cy="ignore-low-stock-warning"
                isSecondary={true}
                data-track="what-do-you-want-to-do"
              >
                {t('What do you want to do?')}
              </Link>
            </>
          }
        />
      </div>
    )
  }

  // Non inventory errors
  let errorMessage: string | null = null

  if (lineItemQuantityError === ERRORS.INVALID_MAX) {
    errorMessage = t('Quantity can’t be greater than 10 digits')
  }

  if (
    !lineItem.confirmed &&
    lineItemQuantityError === ERRORS.INVALID_MIN &&
    isUnfulfilledLineItem(
      getFeatureFromCache('fulfillment_lif'),
      lineItem,
      saleStatus || ''
    )
  ) {
    errorMessage = t('Quantity can’t be negative for unfulfilled items')
  }

  // Return errors
  if (
    userManager.hasPermission(PERMISSIONS.SALE_RETURN) &&
    !isUnfulfilledLineItem(
      getFeatureFromCache('fulfillment_lif'),
      lineItem,
      saleStatus || ''
    )
  ) {
    if (lineItemQuantityError === ERRORS.INVALID_MIN_RETURN_QUANTITY) {
      errorMessage = t(
        'Return quantity can’t be more than original quantity sold'
      )
    }
    if (lineItemQuantityError === ERRORS.INVALID_MAX_RETURN_QUANTITY) {
      errorMessage = t('Return quantity can’t be more than 0')
    }
  }

  if (lineItemQuantityError === ERRORS.INVALID_CHARACTERS) {
    errorMessage = t('Enter a numeric value', {
      _context: 'Error message when the input contains non-numeric characters',
    })
  }

  return errorMessage ? (
    <InputErrorMessage errorElement={<>{errorMessage}</>} />
  ) : null
}
